import { makeStyles } from "tss-react/mui";
import { Grid, FormControl, InputLabel, Select, MenuItem, Button, Divider, ListSubheader } from "@mui/material";
import { useTranslation } from "react-i18next";
import ResponsivePage from "../../common/core/ResponsivePage";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import { useEntityById, useEntities, Compare, useEntityFunctions, StatusCode, useAuth, Task } from "@emberly/zenith-client";
import SimpleTable from "../../common/tables/SimpleTable";
import { getCurrencyShorthand, useStation } from "../../../providers/StationProvider";
import { FixNumber, GetUserReference } from "../../../common/orders";
import { ToggleSurface } from "../../common/core/Surface";
import { ExportEnums, ExportEnumsLists, OrderEnumsLists } from "../../../common/constants";
import { convertCashJournalsToFormat, downloadCSV } from "../../../common/exportformats";
import { IntegrationLibrary } from "../integrations/common/library";
import { useNotification } from "../../../providers/NotificationProvider";
import ExportHistorySection from "../../common/sections/ExportHistorySection";
import moment from "moment/moment";
import ConfirmDialog from "../../common/inputs/ConfirmDialog";
import config from "../../../config";

const useStyles = makeStyles()(
  (theme) => ({
    number: {
      color: theme.palette.text.disabled,
      fontWeight: "500"
    }
  })
);


const FILE_FORMATS_LEN = ExportEnumsLists.CashExportFormats.length;

export default function CashExportPage() {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { exportId } = useParams();

  const { alert, snackbar } = useNotification();

  const { user } = useAuth();
  const { exportSettings } = useStation();

  
  const [resendAction, setResendAction] = useState(null);
  const [format, setFormat] = useState(ExportEnums.CashExportFormats.XL10);
  const { entity: exportEntity, updateEntityField } = useEntityById("CashExport", exportId);

  

  const { entities: journalLines, loading: journalLinesLoading } = useEntities("CashJournal", useMemo(() => ({
    path: "exportId",
    comparer: Compare.EQ,
    value: exportId,
    name: `cashjournals_${exportId}`
  }), [exportId]));

  const { entities: orders, loading: ordersLoading } = useEntities("Order", useMemo(() => ({
    or: [
      {
        path: "payment.terminal.exportId",
        comparer: Compare.EQ,
        value: exportId,
      },
      {
        path: "payment.request.exportId",
        comparer: Compare.EQ,
        value: exportId,
      }
    ],
    name: `orders_cashexport_${exportId}`
  }), [exportId]));

  const { entities: integrationEntities } = useEntities("Integration");
  const { emitServiceEvent: emitIntegrationEvent } = useEntityFunctions("Integration");
  const integrations = useMemo(() => integrationEntities.filter(t => !!IntegrationLibrary.get(t.serviceType)?.capabilities?.ImportCashOrders), [integrationEntities]);
  const [working, setWorking] = useState(false);

  const logExport = useCallback((friendlyName) => {
    updateEntityField("history", [
      ...(exportEntity.history || []),
      {
        friendlyName,
        user: GetUserReference(user),
        created: moment.utc().toISOString()
      }
    ]);
    if (!exportEntity.exported) {
      updateEntityField("exported", true);
    }
  }, [updateEntityField, user, exportEntity]);


  const sendToIntegration = useCallback(async () => {
    try {
      snackbar(t("integrations:common:sending"));
      setWorking(true);
      const selected = integrations[format - FILE_FORMATS_LEN];
      const response = await emitIntegrationEvent("ImportCashOrders", { integrationId: selected.id, exportId })

      if (response.statusCode === StatusCode.Success) {
        snackbar(t("integrations:common:sendingSuccess"), "success");
        logExport(selected.friendlyName);

      } else {
        snackbar(t("integrations:common:sendingError"), "error");

        if (!!response?.value) {
          await alert(
            t(`integrations:errors:${response.message}:title`),
            (response.value.detail || response.value.title || "").replace(/[^A-Z\d\s.:æøåÆØÅ]/gi, ''),
            "error"
          );

        } else if (!!response.message) {
          await alert(
            t(`integrations:library:errors:${response.message}:title`),
            t(`integrations:library:errors:${response.message}:description`),
            "error"
          );
        }
      }

    } catch (err) {
      console.log(err);
    } finally {
      setWorking(false);
    }
  }, [format, integrations, emitIntegrationEvent, exportId, alert, t, snackbar, logExport]);

  const downloadExport = useCallback(() => {
    const data = convertCashJournalsToFormat(format, journalLines, exportSettings);
    downloadCSV(data, `cashexport_${exportEntity.number}.csv`);
    logExport(ExportEnumsLists.CashExportFormats[format]);
  }, [journalLines, format, exportEntity, logExport, exportSettings]);


  const triggerExport = useCallback(async () => {

    if (exportEntity.exported) {
      try {
        const t = new Task();
        setResendAction(t)
        await t.wait();
      } catch {
        return;
      } finally {
        setResendAction(null);
      }
    }

    if (format < FILE_FORMATS_LEN) {
      downloadExport();
    } else {
      sendToIntegration();
    }
  }, [format, downloadExport, sendToIntegration, exportEntity]);


  const journalLinesColumns = useMemo(() => {
    return [
      {
        field: "exportNumber",
        type: "number",
        headerName: t("cashJournal:fieldNumber"),
        flex: 0
      },
      {
        field: "orderNumber",
        type: "number",
        headerName: t("cashJournal:fieldOrderNumber"),
        flex: 0
      },
      {
        field: "row",
        type: "number",
        headerName: t("cashJournal:fieldRow"),
        flex: 0
      },
      {
        field: "item.quantity",
        type: "number",
        entityType: "decimal",
        valueGetter: (_, row) => Number(row.item?.quantity || "1"),
        valueFormatter: value => FixNumber(value),
        headerName: t("cashJournal:fieldQuantity"),
        flex: 0
      },
      {
        field: "unitPrice.value",
        type: "number",
        entityType: "decimal",
        valueGetter: (_, row) => Number(row.unitPrice?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => `${FixNumber(params.row.unitPrice?.value)} ${getCurrencyShorthand(t, params.row.unitPrice?.currency)}`,
        headerName: t("cashJournal:fieldUnitPrice"),
        minWidth: 120,
      },
      {
        field: "costPrice.value",
        type: "number",
        entityType: "decimal",
        valueGetter: (_, row) => Number(row.costPrice?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => `${FixNumber(params.row.costPrice?.value)} ${getCurrencyShorthand(t, params.row.costPrice?.currency)}`,
        headerName: t("cashJournal:fieldCostPrice"),
        minWidth: 120,
      },
      {
        field: "grossPrice.value",
        type: "number",
        entityType: "decimal",
        valueGetter: (_, row) => Number(row.grossPrice?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => `${FixNumber(params.row.grossPrice?.value)} ${getCurrencyShorthand(t, params.row.grossPrice?.currency)}`,
        headerName: t("cashJournal:fieldGrossPrice"),
        minWidth: 120,
      },
      {
        field: "item.code",
        valueGetter: (_, row) => row.item?.code || "-",
        headerName: t("cashJournal:fieldProductCode"),
        flex: 0
      },
      {
        field: "item.name",
        valueGetter: (_, row) => row.item?.name || "-",
        headerName: t("cashJournal:fieldProductName"),
        flex: 0
      },
      {
        field: "sender.name",
        valueGetter: (_, row) => row.sender?.name || "-",
        headerName: t("cashJournal:fieldSender"),
        flex: 1,
      },
      {
        field: "account",
        valueGetter: (_, row) => row.account || "-",
        headerName: t("cashJournal:fieldAccount"),
        flex: 1,
      },
      {
        field: "item.productGroup.vatCode.code",
        valueGetter: (_, row) => row.item?.productGroup?.vatCode?.code || "-",
        headerName: t("cashJournal:fieldVatCode"),
        flex: 1,
      },
    ]
  }, [t]);

  const ordersColumns = useMemo(() => {
    return [
      {
        field: "number",
        type: "number",
        headerName: t("entity:order:number"),
        flex: 0
      },
      {
        field: "missionNumber",
        type: "number",
        headerName: t("common:tables:mission"),
        flex: 0
      },
      {
        field: "customer.name",
        headerName: t("entity:order:customer"),
        valueGetter: (_, row) => row.customer?.name || "-",
        minWidth: 160,
        flex: 0.5,
      },
      {
        field: "payment.method",
        headerName: t("entity:order:payment:method"),
        valueGetter: (_, row) => t(`order:enums:paymentMethod:${OrderEnumsLists.PaymentMethod[row.payment.method]}`),
        minWidth: 160
      },
      {
        field: "payment.calculatedTotal.value",
        type: "number",
        entityType: "decimal",
        headerName: t("entity:order:payment:calculatedTotal"),
        valueGetter: (_, row) => Number(row.payment.calculatedTotal?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => `${FixNumber(params.row.payment.calculatedTotal?.value)} ${getCurrencyShorthand(t, params.row.payment.calculatedTotal?.currency)}`,
        minWidth: 120
      },
      {
        field: "deductible.price.value",
        type: "number",
        entityType: "decimal",
        headerName: t("entity:order:deductible"),
        valueGetter: (_, row) => Number(row.deductible?.price?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => params.row.deductible ? `${FixNumber(params.row.deductible?.price?.value)} ${getCurrencyShorthand(t, params.row.deductible?.price?.currency)}` : "-",
        minWidth: 120
      },
      {
        field: "payment.sender.name",
        headerName: t("entity:order:sender"),
        valueGetter: (_, row) => row.payment.sender?.name || "-",
        minWidth: 160,
        flex: 0.5
      },
    ]
  }, [t]);

  const onOrderRowClick = useCallback((params, ev) => {
    navigate(`./${params.row.missionId}/${params.id}`, { relative: "path" });
  }, [navigate]);


  const onJournalRowClick = useCallback((params, ev) => {
    if (!!params.row.missionId) {
      navigate(`./${params.row.missionId}/${params.row.orderId}`, { relative: "path" });
    }
  }, [navigate]);


  return (
    <ResponsivePage
      title={<>{t("cashExport:title")} <span className={classes.number}>#{exportEntity?.number || ""}</span></>}
      safeTitle={t("cashExport:title")}
      backPath="../"
      maxWidth={config.pageSize.large}
      backButton
    >

      <ToggleSurface
        title={t("cashExport:exportTitle")}
        xs={12}
        lg={6}
      >
        <Grid item xs={12} container spacing={2} alignItems="center">

          <Grid item xs={12}>
            <FormControl fullWidth size="small" variant="filled">
              <InputLabel>{t("cashExport:exportSelect")}</InputLabel>
              <Select onChange={(ev) => setFormat(ev.target.value)} value={format}>

                <ListSubheader>{t("cashExport:filesSubHeader")}</ListSubheader>
                {ExportEnumsLists.CashExportFormats.map((type, value) => <MenuItem value={value} key={value}>{type}</MenuItem>)}

                {!!integrations.length ? <Divider /> : null}
                {!!integrations.length ? <ListSubheader>{t("cashExport:integrationsSubHeader")}</ListSubheader> : null}
                {integrations.map((ig, value) => <MenuItem value={value + FILE_FORMATS_LEN} key={value}>{ig.friendlyName}</MenuItem>)}

              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Button variant="contained" fullWidth size="large" onClick={triggerExport} disabled={journalLinesLoading || working || !exportSettings}>
              {working ? t("cashExport:working") : format < FILE_FORMATS_LEN ? t("common:download") : t("cashExport:sendButton")}
            </Button>
          </Grid>

        </Grid>
      </ToggleSurface>

      <ConfirmDialog
        task={resendAction}
        title={t("integrations:resend:title")}
        description={t("integrations:resend:description")}
        confirmText={t("integrations:resend:confirmText")}
      />


      <ToggleSurface
        title={!!exportEntity?.history?.length ? t("cashExport:history") : null}
        xs={12}
        lg={6}
      >
        <ExportHistorySection
          loading={!exportEntity}
          history={exportEntity?.history || []}
        />
      </ToggleSurface>


      <ToggleSurface title={t("cashExport:orders")}>
        <SimpleTable
          tableId="cashexport_orders"
          loading={ordersLoading}
          onRowClick={onOrderRowClick}
          columns={ordersColumns}
          rows={orders}
          emptyStateTitle={t("cashExport:ordersEmptyStateTitle")}
          emptyStateDescription={t("cashExport:ordersEmptyStateDescription")}
          grow
          xs={12}
        />
      </ToggleSurface>

      <ToggleSurface title={t("cashExport:journalLines")}>
        <SimpleTable
          tableId="cashexport_journallines"
          loading={journalLinesLoading}
          onRowClick={onJournalRowClick}
          columns={journalLinesColumns}
          rows={journalLines}
          emptyStateTitle={t("cashJournal:emptyStateTitle")}
          emptyStateDescription={t("cashJournal:emptyStateDescription")}
          xs={12}
        />
      </ToggleSurface>

    </ResponsivePage>
  );
}

